import type { AgencyVisibility } from '@saaga/shared/interfaces/agency.interface'
import { isAgencyEditable } from '@saaga/shared/utils/agency-visibility.util'
import { Loader2Icon, PencilIcon, SendIcon, UndoIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

interface VisibilityActionButtonProps {
  isLoading: boolean
  onClick: () => void
}

const SubmitForReviewButton = ({ isLoading, onClick }: VisibilityActionButtonProps): ReactNode => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        size="sm"
        className="agency-submit-review-button gap-2"
        onClick={onClick}
        disabled={isLoading}
      >
        {isLoading && <Loader2Icon className="size-4 animate-spin" />}
        <SendIcon className="size-4" />
        Submit for Public Review
      </Button>
    </TooltipTrigger>
    <TooltipContent>
      Your agency will be reviewed by an admin before appearing on the marketplace
    </TooltipContent>
  </Tooltip>
)

const CancelReviewButton = ({ isLoading, onClick }: VisibilityActionButtonProps): ReactNode => (
  <Button
    variant="outline"
    size="sm"
    className="agency-cancel-review-button gap-2"
    onClick={onClick}
    disabled={isLoading}
  >
    {isLoading && <Loader2Icon className="size-4 animate-spin" />}
    <UndoIcon className="size-4" />
    Cancel Review
  </Button>
)

const EditAgencyButton = ({ isLoading, onClick }: VisibilityActionButtonProps): ReactNode => (
  <Button
    variant="outline"
    size="sm"
    className="agency-edit-button gap-2"
    onClick={onClick}
    disabled={isLoading}
  >
    <PencilIcon className="size-4" />
    Edit
  </Button>
)

interface AgencyDetailVisibilityActionsProps {
  visibility: AgencyVisibility
  isLoading?: boolean
  onSubmitForReview: () => void
  onCancelReview: () => void
  onEdit: () => void
}

/**
 * Renders visibility-dependent action buttons for the agency detail page.
 * Actions are determined by the current visibility state machine.
 */
export const AgencyDetailVisibilityActions = ({
  visibility,
  isLoading = false,
  onSubmitForReview,
  onCancelReview,
  onEdit,
}: AgencyDetailVisibilityActionsProps): ReactNode => (
  <div className="agency-detail-visibility-actions flex items-center gap-2">
    {visibility === 'private' && (
      <SubmitForReviewButton isLoading={isLoading} onClick={onSubmitForReview} />
    )}
    {visibility === 'pending_review' && (
      <CancelReviewButton isLoading={isLoading} onClick={onCancelReview} />
    )}
    {isAgencyEditable(visibility) && visibility !== 'draft' && (
      <EditAgencyButton isLoading={isLoading} onClick={onEdit} />
    )}
  </div>
)

AgencyDetailVisibilityActions.displayName = 'AgencyDetailVisibilityActions'
